import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, PhoneOff, Mic, MicOff, Loader2, Sparkles, X, PhoneOutgoing, PhoneIncoming,
  CheckCircle2, AlertTriangle, NotebookPen, ArrowRight, AudioLines, Trash2,
  Minus, Maximize2, Grid3x3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCall } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { ContactLinkDialog } from "@/components/ContactLinkDialog";

function fmtDuration(s) {
  const m = Math.floor((s || 0) / 60);
  const sec = (s || 0) % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

const STATUS_META = {
  incoming: { label: "Appel entrant…", color: "#1F9D55", pulse: true },
  connecting: { label: "Connexion…", color: "var(--shariti-orange)", pulse: true },
  ringing: { label: "Sonnerie…", color: "var(--shariti-orange)", pulse: true },
  "in-call": { label: "En communication", color: "#1F9D55", pulse: true },
  ended: { label: "Appel terminé", color: "rgba(24,24,24,0.55)", pulse: false },
  error: { label: "Échec de l'appel", color: "#D64545", pulse: false },
};

const SENTIMENT = {
  positif: { label: "Positif", bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F" },
  neutre: { label: "Neutre", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.66)" },
  "négatif": { label: "Négatif", bg: "rgba(214,69,69,0.12)", fg: "#B23434" },
};

export function CallWidget() {
  const { status, meta, muted, durationSec, widgetOpen, hangup, toggleMute, sendDigit, closeWidget, acceptIncoming, rejectIncoming } = useCall();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dialed, setDialed] = useState("");
  const [pipeline, setPipeline] = useState({ transcription_status: null, hasTranscript: false });
  // Rattachement forcé d'un appel « orphelin » (numéro composé sans magasin associé).
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkResolved, setLinkResolved] = useState(false);
  const isOrphanCall = status === "ended" && !!meta?.logId && !meta?.spotId && meta?.direction !== "inbound";
  useEffect(() => { setLinkResolved(false); setLinkOpen(false); }, [meta?.logId]);
  useEffect(() => {
    if (isOrphanCall && !linkResolved) setLinkOpen(true);
  }, [isOrphanCall, linkResolved]);

  // Réinitialise le compte-rendu à chaque nouvel appel.
  useEffect(() => {
    if (status === "connecting") {
      setNotes(""); setAnalysis(null); setMinimized(false); setShowKeypad(false); setDialed("");
      setPipeline({ transcription_status: null, hasTranscript: false });
    }
  }, [status]);

  // Après l'appel : suit le pipeline AUTOMATIQUE (enregistrement → transcription
  // Deepgram → analyse IA) et affiche le compte rendu dès qu'il est prêt.
  useEffect(() => {
    if (status !== "ended" || !meta?.logId) return;
    let alive = true;
    let tries = 0;
    const tick = async () => {
      if (!alive) return;
      tries += 1;
      try {
        const { data } = await api.get(`/calls/${meta.logId}`);
        if (!alive) return;
        const hasTranscript = !!(data.transcript || (Array.isArray(data.transcript_segments) && data.transcript_segments.length));
        setPipeline({ transcription_status: data.transcription_status, hasTranscript });
        if (data.ai_summary) {
          setAnalysis((prev) => prev || { summary: data.ai_summary, analysis: data.ai_analysis });
        }
        const done = data.ai_summary || tries >= 30 ||
          ["disabled", "no_recording", "error"].includes(data.transcription_status);
        if (done) clearInterval(id);
      } catch { /* silencieux : on retente au prochain tick */ }
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => { alive = false; clearInterval(id); };
  }, [status, meta?.logId]);

  if (!widgetOpen || !meta) return null;

  const sm = STATUS_META[status] || STATUS_META.connecting;
  const isInbound = meta.direction === "inbound";
  const initials = (meta.contactName || meta.spotName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isIncoming = status === "incoming";
  const inLiveCall = ["connecting", "ringing", "in-call"].includes(status);

  const pressDigit = (d) => {
    sendDigit(d);
    setDialed((s) => (s + d).slice(-20));
  };

  const saveAndAnalyze = async () => {
    setSaving(true);
    try {
      if (notes.trim()) {
        await api.put(`/calls/${meta.logId}/notes`, { cdp_notes: notes });
      }
      const { data } = await api.post(`/calls/${meta.logId}/analyze`);
      setAnalysis(data);
      toast.success("Analyse IA générée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'analyser l'appel.");
    } finally { setSaving(false); }
  };
  const saveNotesOnly = async () => {
    setSaving(true);
    try {
      await api.put(`/calls/${meta.logId}/notes`, { cdp_notes: notes });
      toast.success("Notes enregistrées");
    } catch { toast.error("Erreur d'enregistrement."); } finally { setSaving(false); }
  };
  const deleteThisCall = async () => {
    setDeleting(true);
    try {
      await api.delete(`/calls/${meta.logId}`);
      toast.success("Appel supprimé définitivement");
      closeWidget();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally { setDeleting(false); }
  };

  // --- Mode RÉDUIT : petite pastille flottante qui NE BLOQUE PAS les boutons ---
  // Permet au CDP de continuer à cliquer dans le script / envoyer un e-mail
  // pendant l'appel. Un clic sur la pastille rouvre le widget complet.
  if (minimized && (inLiveCall || isIncoming)) {
    const sm2 = STATUS_META[status] || STATUS_META.connecting;
    return (
      <AnimatePresence>
        <motion.div
          key="call-pill"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-5 right-5 z-[300] flex items-center gap-2 rounded-full border border-[var(--border-soft)] bg-white py-2 pl-3 pr-2 shadow-[var(--shadow-lg)]"
          data-testid="call-widget-pill"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            {sm2.pulse && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: sm2.color }} />}
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: sm2.color }} />
          </span>
          <span className="max-w-[120px] truncate text-[12px] font-bold text-ink" data-testid="call-pill-contact">
            {meta.contactName || meta.spotName || meta.toNumber || "Appel"}
          </span>
          {(status === "in-call") && (
            <span className="font-mono text-[12px] font-bold tabular-nums text-muted-ink">{fmtDuration(durationSec)}</span>
          )}
          <button onClick={() => setMinimized(false)} data-testid="call-widget-expand" aria-label="Agrandir l'appel"
            className="grid h-8 w-8 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink">
            <Maximize2 className="h-4 w-4" />
          </button>
          {inLiveCall && (
            <button onClick={hangup} data-testid="call-pill-hangup" aria-label="Raccrocher"
              className="grid h-8 w-8 place-items-center rounded-full bg-[#D64545] text-white transition-transform hover:scale-105 active:scale-95">
              <PhoneOff className="h-4 w-4" />
            </button>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <>
    <AnimatePresence>
      <motion.div
        key="call-widget"
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-5 right-5 z-[300] w-[360px] max-w-[calc(100vw-2.5rem)] overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-lg)]"
        data-testid="call-widget"
      >
        {/* En-tête coloré */}
        <div className="relative px-4 py-3.5" style={{ background: "linear-gradient(135deg, rgba(238,114,46,0.14), rgba(248,159,99,0.06))" }}>
          <div className="flex items-center gap-3">
            <div className="relative grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-sm font-bold text-white">
              {isInbound ? <PhoneIncoming className="h-5 w-5" /> : initials}
              {sm.pulse && (
                <span className="absolute inset-0 animate-ping rounded-full opacity-30" style={{ background: sm.color }} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="break-words text-sm font-extrabold" data-testid="call-widget-contact">
                {isInbound ? (meta.contactName && meta.contactName !== meta.fromNumber ? meta.contactName : "Appel entrant") : (meta.contactName || meta.spotName || "Appel")}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-ink">
                {isInbound ? <PhoneIncoming className="h-3 w-3" /> : <PhoneOutgoing className="h-3 w-3" />}
                <span className="truncate">{meta.toNumber}</span>
              </div>
            </div>
            {(inLiveCall || isIncoming) && (
              <button onClick={() => setMinimized(true)} data-testid="call-widget-minimize"
                className="rounded-lg p-1.5 text-muted-ink transition-colors hover:bg-white/70 hover:text-ink" aria-label="Réduire">
                <Minus className="h-4 w-4" />
              </button>
            )}
            {!inLiveCall && !isIncoming && (
              <button onClick={() => { closeWidget(); }} data-testid="call-widget-close"
                className="rounded-lg p-1.5 text-muted-ink transition-colors hover:bg-white/70 hover:text-ink" aria-label="Fermer">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-2.5 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: sm.color }} data-testid="call-widget-status">
              {status === "connecting" || status === "ringing" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {status === "in-call" ? <span className="h-2 w-2 rounded-full" style={{ background: sm.color }} /> : null}
              {status === "error" ? <AlertTriangle className="h-3 w-3" /> : null}
              {status === "ended" ? <CheckCircle2 className="h-3 w-3" /> : null}
              {sm.label}
            </span>
            {(status === "in-call" || status === "ended") && (
              <span className="font-mono text-sm font-bold tabular-nums" data-testid="call-widget-timer">{fmtDuration(durationSec)}</span>
            )}
          </div>
        </div>

        {/* Appel entrant : répondre / refuser */}
        {isIncoming && (
          <div className="px-4 py-5" data-testid="call-widget-incoming">
            <div className="mb-4 text-center text-[13px] font-semibold text-muted-ink">
              Un correspondant vous appelle…
            </div>
            <div className="flex items-center justify-center gap-8">
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={rejectIncoming}
                  data-testid="call-widget-reject"
                  aria-label="Refuser l'appel"
                  className="grid h-14 w-14 place-items-center rounded-full bg-[#D64545] text-white shadow-[var(--shadow-md)] transition-transform hover:scale-105 active:scale-95"
                >
                  <PhoneOff className="h-6 w-6" />
                </button>
                <span className="text-[11px] font-semibold text-muted-ink">Refuser</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={acceptIncoming}
                  data-testid="call-widget-accept"
                  aria-label="Répondre à l'appel"
                  className="grid h-14 w-14 place-items-center rounded-full bg-[#1F9D55] text-white shadow-[var(--shadow-md)] transition-transform hover:scale-105 active:scale-95"
                >
                  <Phone className="h-6 w-6" />
                </button>
                <span className="text-[11px] font-semibold text-muted-ink">Répondre</span>
              </div>
            </div>
          </div>
        )}

        {/* Contrôles en appel */}
        {inLiveCall && (
          <div className="px-4 py-4">
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={toggleMute}
                disabled={status !== "in-call"}
                data-testid="call-widget-mute"
                className={`grid h-12 w-12 place-items-center rounded-full border transition-colors disabled:opacity-40 ${
                  muted ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"
                        : "border-[var(--border-soft)] bg-white text-ink hover:bg-[var(--surface-2)]"}`}
                aria-label={muted ? "Réactiver le micro" : "Couper le micro"}
              >
                {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
              <button
                onClick={hangup}
                data-testid="call-widget-hangup"
                className="grid h-14 w-14 place-items-center rounded-full bg-[#D64545] text-white shadow-[var(--shadow-md)] transition-transform hover:scale-105 active:scale-95"
                aria-label="Raccrocher"
              >
                <PhoneOff className="h-6 w-6" />
              </button>
              <button
                onClick={() => setShowKeypad((o) => !o)}
                disabled={status !== "in-call"}
                data-testid="call-widget-keypad-toggle"
                className={`grid h-12 w-12 place-items-center rounded-full border transition-colors disabled:opacity-40 ${
                  showKeypad ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"
                             : "border-[var(--border-soft)] bg-white text-ink hover:bg-[var(--surface-2)]"}`}
                aria-label="Clavier de numérotation"
              >
                <Grid3x3 className="h-5 w-5" />
              </button>
            </div>

            {/* Clavier DTMF : « tapez 1 pour l'accueil », « 2 pour la direction »… */}
            {showKeypad && (
              <div className="mt-4" data-testid="call-widget-keypad">
                <div className="mb-2 flex h-7 items-center justify-center rounded-lg bg-[var(--surface-2)] px-3 font-mono text-sm font-bold tracking-widest text-ink" data-testid="call-widget-dialed">
                  {dialed || <span className="text-[11px] font-normal tracking-normal text-muted-ink">Touches envoyées…</span>}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) => (
                    <button
                      key={k}
                      onClick={() => pressDigit(k)}
                      disabled={status !== "in-call"}
                      data-testid={`dtmf-${k}`}
                      className="grid h-11 place-items-center rounded-xl border border-[var(--border-soft)] bg-white text-lg font-bold text-ink transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] active:scale-95 disabled:opacity-40"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-center text-[10px] text-muted-ink">
                  Serveur vocal : composez la touche demandée (ex. « tapez 1 pour l'accueil, 2 pour la direction »).
                </p>
              </div>
            )}
          </div>
        )}

        {/* Compte-rendu post-appel */}
        {(status === "ended" || status === "error") && (
          <div className="space-y-3 px-4 py-4">
            {status === "ended" && <PipelineStatus pipeline={pipeline} hasAnalysis={!!analysis} />}

            {isOrphanCall && (
              <div className={`rounded-xl border px-3 py-2.5 ${linkResolved ? "border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.06)]" : "border-[rgba(238,114,46,0.5)] bg-[rgba(238,114,46,0.08)]"}`} data-testid="orphan-link-banner">
                {linkResolved ? (
                  <div className="flex items-center gap-2 text-[12px] font-semibold text-[#1F7A44]">
                    <CheckCircle2 className="h-4 w-4" /> Contact rattaché — aucune donnée perdue.
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-start gap-2 text-[12px] font-semibold text-[#9A4A17]">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Ce numéro n'est associé à aucun magasin. Rattachez-le pour ne perdre aucun contact.</span>
                    </div>
                    <Button size="sm" onClick={() => setLinkOpen(true)} data-testid="orphan-link-open"
                      className="shrink-0 bg-[var(--shariti-orange)] hover:bg-[#E56622]">Rattacher</Button>
                  </div>
                )}
              </div>
            )}

            {analysis && <AiSummaryCompact analysis={analysis} />}

            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                <NotebookPen className="h-3.5 w-3.5" /> Notes de l'appel {analysis && <span className="font-normal normal-case text-muted-ink">(ajoutez des précisions puis ré-analysez)</span>}
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Qu'a dit l'interlocuteur ? Points clés, objections, prochaine étape…"
                className="mt-2 min-h-[80px] resize-none text-sm"
                data-testid="call-widget-notes"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveAndAnalyze} disabled={saving || (!notes.trim() && !pipeline.hasTranscript)}
                className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] disabled:opacity-50" data-testid="call-widget-analyze">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                {analysis ? "Ré-analyser" : "Analyser avec l'IA"}
              </Button>
              <Button variant="outline" onClick={saveNotesOnly} disabled={saving} data-testid="call-widget-save-notes">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Enregistrer
              </Button>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => { navigate("/appels"); closeWidget(); }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline"
                data-testid="call-widget-history-link"
              >
                Voir l'historique des appels <ArrowRight className="h-3 w-3" />
              </button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink transition-colors hover:text-[#D64545] ${isAdmin ? "" : "hidden"}`}
                    data-testid="call-widget-delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />Supprimer
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="call-widget-delete-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer cet appel ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      L'appel, son <strong>enregistrement</strong>, sa transcription et son analyse seront
                      <strong> définitivement supprimés</strong> (y compris côté Twilio). Irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); deleteThisCall(); }}
                      disabled={deleting}
                      className="bg-[#D64545] text-white hover:bg-[#b93a3a]"
                      data-testid="call-widget-delete-confirm-btn"
                    >
                      {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
                      Supprimer définitivement
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
    {meta?.logId && (
      <ContactLinkDialog
        call={{ id: meta.logId, to_number: meta.toNumber }}
        open={linkOpen}
        forced={isOrphanCall && !linkResolved}
        onClose={() => setLinkOpen(false)}
        onLinked={() => { setLinkResolved(true); setLinkOpen(false); }}
      />
    )}
    </>
  );
}

/** Bandeau d'état du pipeline automatique post-appel (enregistrement → transcription → IA). */
function PipelineStatus({ pipeline, hasAnalysis }) {
  const st = pipeline.transcription_status;
  let label, spin = false, tone = "neutral";
  if (hasAnalysis) { label = "Analyse IA prête"; tone = "green"; }
  else if (st === "done" || pipeline.hasTranscript) { label = "Transcription prête — analyse IA en cours…"; spin = true; }
  else if (st === "pending" || st === "processing") { label = "Transcription en cours…"; spin = true; }
  else if (st === "error") { label = "Transcription indisponible — analysez vos notes"; tone = "warn"; }
  else if (st === "disabled") { label = "Transcription désactivée"; tone = "neutral"; }
  else if (st === "no_recording") { label = "Aucun enregistrement pour cet appel"; tone = "neutral"; }
  else { label = "Traitement de l'enregistrement…"; spin = true; }

  const toneCls = tone === "green"
    ? "bg-[rgba(31,157,85,0.1)] text-[#0F5A2F]"
    : tone === "warn"
      ? "bg-[rgba(240,190,20,0.18)] text-[#5A4200]"
      : "bg-[var(--surface-2)] text-muted-ink";
  return (
    <div className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${toneCls}`} data-testid="call-widget-pipeline-status">
      {spin ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : hasAnalysis ? <CheckCircle2 className="h-3.5 w-3.5" />
          : <AudioLines className="h-3.5 w-3.5" />}
      {label}
    </div>
  );
}

function AiSummaryCompact({ analysis }) {
  const a = analysis?.analysis || {};
  const s = SENTIMENT[a.sentiment] || SENTIMENT.neutre;
  return (
    <div className="space-y-2.5" data-testid="call-widget-ai-summary">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">
        <Sparkles className="h-3.5 w-3.5" /> Analyse IA
      </div>
      <p className="rounded-xl bg-[var(--surface-2)] p-3 text-sm leading-relaxed">{analysis.summary}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
        {a.outcome && <span className="rounded-full bg-[rgba(238,114,46,0.1)] px-2 py-0.5 text-[11px] font-bold capitalize text-[var(--shariti-orange)]">{String(a.outcome).replaceAll("_", " ")}</span>}
        {typeof a.score === "number" && <span className="ml-auto text-[11px] font-bold text-muted-ink">Score : {a.score}/100</span>}
      </div>
      {a.next_action && (
        <div className="rounded-xl border border-[var(--border-soft)] px-3 py-2 text-xs">
          <span className="font-bold text-ink">Prochaine action : </span>
          <span className="text-muted-ink">{a.next_action}</span>
        </div>
      )}
    </div>
  );
}
